import type { Particle, Intensity, ParticleCustomization, IntensityColorScheme } from '../types';

/**
 * Base class for season-specific particle effects
 */
export abstract class ParticleEffect {
    protected particles: Particle[] = [];
    protected intensity: Intensity;
    protected variant: string;
    protected customization: ParticleCustomization;
    protected defaultColorScheme: IntensityColorScheme;
    protected loadedImages: HTMLImageElement[] = [];
    protected imagesLoaded: boolean = false;
    private imagesAssignedOnce: boolean = false;

    constructor(intensity: Intensity = 'subtle', variant?: string, customization?: ParticleCustomization) {
        this.intensity = intensity;
        this.variant = variant || this.getDefaultVariant();
        this.customization = customization || {};
        this.defaultColorScheme = this.getDefaultColorScheme();
        // Start loading images asynchronously, then update particles when ready
        this.loadCustomImages().then(() => {
            // If particles exist, update them with images
            if (this.particles.length > 0) {
                this.assignImagesToParticles();
            }
        });
    }

    /**
     * Get the default variant for this effect
     */
    protected abstract getDefaultVariant(): string;

    /**
     * Get default color scheme for this effect
     */
    protected abstract getDefaultColorScheme(): IntensityColorScheme;

    /**
     * Get color based on intensity
     */
    protected getIntensityColor(): string {
        if (this.customization.customColor) {
            return this.customization.customColor;
        }

        if (!this.customization.intensityColors) {
            return this.defaultColorScheme[this.intensity];
        }

        const scheme = {
            ...this.defaultColorScheme,
            ...this.customization.colorScheme,
        };

        return scheme[this.intensity];
    }

    /**
     * Get speed multiplier
     */
    protected getSpeedMultiplier(): number {
        return this.customization.speedMultiplier ?? 1.0;
    }

    /**
     * Get size multiplier
     */
    protected getSizeMultiplier(): number {
        return this.customization.sizeMultiplier ?? 1.0;
    }

    /**
     * Get particle count based on intensity
     */
    protected getParticleCount(): number {
        const counts = {
            minimal: 10,
            subtle: 30,
            moderate: 60,
            bold: 100,
        };
        return counts[this.intensity] || 30;
    }

    /**
     * Load custom images if provided
     */
    protected async loadCustomImages(): Promise<void> {
        const images: string[] = [];

        if (this.customization.customImage) {
            images.push(this.customization.customImage);
        } else if (this.customization.customImages && this.customization.customImages.length > 0) {
            images.push(...this.customization.customImages);
        }

        if (images.length === 0) {
            this.imagesLoaded = true;
            return;
        }

        const loadPromises = images.map((src) => {
            return new Promise<HTMLImageElement>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve(img);
                img.onerror = () => {
                    console.warn(`Failed to load custom particle image: ${src}`);
                    resolve(img); // Resolve anyway to not block
                };
                img.src = src;
            });
        });

        try {
            const allImages = await Promise.all(loadPromises);
            // Filter out images that failed to load (naturalHeight will be 0)
            this.loadedImages = allImages.filter(img => img.complete && img.naturalHeight > 0);
            this.imagesLoaded = true;

            if (this.loadedImages.length === 0) {
                console.warn('No custom images loaded successfully');
            }
        } catch (error) {
            console.error('Error loading custom images:', error);
            this.imagesLoaded = true;
        }
    }

    /**
     * Get random loaded image for particle
     */
    protected getRandomImage(): HTMLImageElement | undefined {
        if (!this.hasCustomImages()) return undefined;
        const validImages = this.loadedImages.filter(img => img.complete && img.naturalHeight > 0);
        if (validImages.length === 0) return undefined;
        const index = Math.floor(Math.random() * validImages.length);
        return validImages[index];
    }

    /**
     * Get loaded image by index
     */
    protected getImageByIndex(index: number): HTMLImageElement | undefined {
        if (index >= 0 && index < this.loadedImages.length) {
            return this.loadedImages[index];
        }
        return undefined;
    }

    /**
     * Check if custom images are enabled and loaded
     */
    protected hasCustomImages(): boolean {
        if (!this.imagesLoaded) return false;
        // Double-check that we have valid loaded images
        const validImages = this.loadedImages.filter(img => img.complete && img.naturalHeight > 0);
        return validImages.length > 0;
    }

    /**
     * Assign images to existing particles
     */
    protected assignImagesToParticles(): void {
        if (!this.hasCustomImages()) return;

        for (const particle of this.particles) {
            if (!particle.image) {
                particle.image = this.getRandomImage();
            }
        }
    }

    /**
     * Ensure particles have images assigned if available
     * Call this during update to handle particles created before images loaded
     */
    protected ensureParticleImages(): void {
        if (!this.hasCustomImages()) return;

        // Check if any particles are missing images
        let missingCount = 0;
        for (const particle of this.particles) {
            if (!particle.image) {
                particle.image = this.getRandomImage();
                missingCount++;
            }
        }

        // Log once when images are first assigned after loading
        if (missingCount > 0 && !this.imagesAssignedOnce) {
            console.log(`Custom images loaded and assigned to ${missingCount} particles`);
            this.imagesAssignedOnce = true;
        }
    }

    /**
     * Render a particle with image support
     */
    protected renderParticle(ctx: CanvasRenderingContext2D, p: Particle): void {
        ctx.save();
        ctx.globalAlpha = p.opacity;

        // Use custom image if available
        if (p.image && p.image.complete && p.image.naturalHeight > 0) {
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);

            const imageMode = this.customization.imageMode || 'contain';
            const drawSize = p.size * 2; // diameter

            if (imageMode === 'fill') {
                // Stretch to fit
                ctx.drawImage(p.image, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
            } else if (imageMode === 'contain') {
                // Maintain aspect ratio, fit within size
                const aspectRatio = p.image.width / p.image.height;
                let drawWidth = drawSize;
                let drawHeight = drawSize;

                if (aspectRatio > 1) {
                    drawHeight = drawSize / aspectRatio;
                } else {
                    drawWidth = drawSize * aspectRatio;
                }

                ctx.drawImage(p.image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
            } else {
                // 'cover': Fill the entire area, cropping as needed
                const aspectRatio = p.image.width / p.image.height;
                let drawWidth = drawSize;
                let drawHeight = drawSize;

                if (aspectRatio > 1) {
                    drawWidth = drawSize * aspectRatio;
                } else {
                    drawHeight = drawSize / aspectRatio;
                }

                ctx.drawImage(p.image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
            }
        } else {
            // Fallback to default circle rendering
            ctx.fillStyle = p.color || '#ffffff';
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }

    /**
     * Update customization settings
     */
    updateCustomization(customization: ParticleCustomization): void {
        this.customization = { ...this.customization, ...customization };
        // Reload images if image settings changed
        if (customization.customImage !== undefined || customization.customImages !== undefined) {
            this.loadCustomImages().then(() => {
                // Update existing particles with new images
                this.assignImagesToParticles();
            });
        }
    }

    /**
     * Initialize particles
     */
    abstract init(width: number, height: number): void;

    /**
     * Update particle positions
     */
    abstract update(deltaTime: number, width: number, height: number): void;

    /**
     * Render particles to canvas
     */
    abstract render(ctx: CanvasRenderingContext2D): void;

    /**
     * Get current particles
     */
    getParticles(): Particle[] {
        return this.particles;
    }

    /**
     * Clear all particles
     */
    clear(): void {
        this.particles = [];
    }
}
